'use client';

import { type JSX, useMemo } from 'react';
import { AudioIcon } from './audio-icon';
import { loadTitleAudioCached } from './cached-loaders';
import { KaraokeText } from './karaoke-text';
import { splitWords } from './split-words';
import { useActiveWordIndex } from './use-active-word-index';
import { useKaraokeAudio } from './use-karaoke-audio';

interface IKaraokeTitleHeroProps {
  slug: string;
  title: string;
  initialAudioUrl: string | null;
  language?: string;
  className?: string;
  buttonLabel?: string;
}

export function KaraokeTitleHero({
  slug,
  title,
  initialAudioUrl,
  language,
  className,
  buttonLabel = 'Pronunciation',
}: IKaraokeTitleHeroProps): JSX.Element {
  const { audioRef, wordTimings, isPlaying, isLoading, toggle, audioHandlers } = useKaraokeAudio(
    () => loadTitleAudioCached(slug),
  );
  const words = useMemo(() => splitWords(title), [title]);
  const active = useActiveWordIndex(audioRef, wordTimings, words);
  const interactive = Boolean(initialAudioUrl);

  return (
    <div className="flex flex-wrap items-center gap-2.5">
      <h1 className={className} lang={language}>
        {interactive ? (
          <button
            aria-label={buttonLabel}
            className="inline-flex cursor-pointer items-center gap-2.5 rounded-md transition-colors hover:bg-foreground/[0.04] focus-visible:outline-2 focus-visible:outline-foreground/40"
            onClick={toggle}
            type="button"
          >
            <AudioIcon iconClassName="size-5" isLoading={isLoading} isPlaying={isPlaying} />
            <KaraokeText active={active} words={words} />
          </button>
        ) : (
          <KaraokeText active={active} words={words} />
        )}
      </h1>
      {initialAudioUrl && (
        <audio preload="none" ref={audioRef} src={initialAudioUrl} {...audioHandlers}>
          <track kind="captions" />
        </audio>
      )}
    </div>
  );
}
